import dayjs from "dayjs"
import {
  Enum_Completion_Status_Enum,
  NudgeScheduleFragment,
  QuestionnaireScheduleFragment,
} from "../generated/graphql"
import { isNudgeSchedule } from "./helpers"
import { ScheduleItem } from "./types"

export interface Take {
  __typename?: "nudge_takes" | "questionnaire_takes" | undefined
  id: number
  created_at: any
  completed_at?: any
  scheduled_at: any
  completion_status?: any
}

const SYSTEM_ACCOUNT_ID = "12e92446-f5ab-454c-a567-4f68a02e2ee6"

/**
 * Takes a list of nudges or questionnaires and calculates whether there was a
 * take scheduled for the specified date that was completed.
 * @param date: a dayjs parsable string that represents the date in question
 * @param takes: a homogeneous array of questionnaire or nudge takes
 * @param isNudgeSched: a boolean indicating whether the takes are from a nudge schedule
 * @returns: the completed_at timestamp of the matching take if one exists, otherwise undefined
 */
export const takeCompletionOnDate = (
  date: string,
  takes: Take[],
  isNudgeSched: boolean
) => {
  const takeOnRefDay = takes.find((take) => {
    if (isNudgeSched) {
      // nudges are completed if completed_at is populated (truthy)
      return dayjs(take.scheduled_at).isSame(date, "day") && take.completed_at
    } else {
      // questionnares are completed if the completion status is complete
      return (
        dayjs(take.scheduled_at).isSame(date, "day") &&
        take.completion_status === Enum_Completion_Status_Enum.Complete
      )
    }
  })
  return takeOnRefDay?.completed_at
}

/**
 * Extracts a normalised display data object from a nudge or questionnaire schedule item.
 * @param item: a NudgeScheduleFragment or QuestionnaireScheduleFragment
 * @returns: an object with id, name, scheduledAt, takes, and (for nudges) message
 */
export const getItemDataFromScheduleItem = (
  item: NudgeScheduleFragment | QuestionnaireScheduleFragment
) => {
  if (isNudgeSchedule(item)) {
    return {
      id: item.id,
      name: item.nudge?.technique ?? "Nudge", // handles edge case where practitioner doesn't have permission to see nudge
      scheduledAt: item.scheduled_at,
      message: item.message,
      takes: item.nudge_takes,
    }
  } else {
    return {
      id: item.id,
      name: item.questionnaire.name,
      scheduledAt: item.scheduled_at,
      takes: item.questionnaire_takes,
    }
  }
}

/**
 * Determines whether a schedule item is a nudge that has been deactivated but is still
 * scheduled for a future date.
 * @param item: a ScheduleItem (nudge or questionnaire schedule)
 * @returns: true if the item is an inactive nudge with a future scheduled date, false otherwise
 */
export const isInactiveInFuture = (item: ScheduleItem) =>
  isNudgeSchedule(item) &&
  item.nudge && // handles edge case where practitioner doesn't have permission to see nudge
  !item.nudge.active &&
  dayjs().hour(0).minute(0).second(0).isBefore(dayjs(item.scheduled_at))

/**
 * Determines whether editing or removing a schedule item should be disabled.
 * Changes are blocked for system-account items and one-time schedules that already
 * have at least one take (i.e. a notification has already been sent).
 * Recurring schedules remain editable.
 * @param item: a NudgeScheduleFragment or QuestionnaireScheduleFragment
 * @returns: true if the item should not be modified, false otherwise
 */
export const noChangeAllowed = (
  item: NudgeScheduleFragment | QuestionnaireScheduleFragment
) => {
  if (item.practitioner_id === SYSTEM_ACCOUNT_ID) {
    return true // System Account refer VIRA-2749
  }
  if (!item.one_time) {
    return false
  }

  // The scheduler always inserts a take when it sends out a nudge or a questionnaire,
  // which is why we can check for takes rather than compare times directly
  const isNudge = isNudgeSchedule(item)
  if (isNudge)
    return (
      item.nudge_takes.length > 0 // notification already sent
    )

  return item.questionnaire_takes.length > 0 // notification already sent
}

export const isSystemAccountPractitioner = (
  item: NudgeScheduleFragment | QuestionnaireScheduleFragment
) => item.practitioner_id === SYSTEM_ACCOUNT_ID
